import { AuthedPage, HeaderFooterLayout } from '../../app';
import { InlineFormInput, Button, FormsCheckBox } from '@platform/components';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { GreenCircleCheckmark } from '../../svg/GreenCircleCheckmark';
import { Organizations } from '@platform/client';
import { Link } from 'wouter';
import { ROUTES } from '../../routes/consts';

type InviteOrgColleagueFormValues = {
  firstname: string;
  lastname: string;
  email: string;
  makeAdmin: boolean;
  removeRepresentative: boolean;
};

export const InviteOrgColleaguePage = () => {
  const [pageStage, setPageState] = useState<'invite' | 'success' | 'error'>('invite');
  return (
    <AuthedPage>
      <HeaderFooterLayout>
        <div className='w-full h-full flex flex-col gap-4 md:justify-center items-center overflow-y-auto'>
          <h2 className='text-display-sm md:text-display-md'>Add a Team Member</h2>
          {pageStage === 'invite' && <InviteColleagueForm setPageState={setPageState} />}
          {pageStage === 'success' && <ConfirmationMessage />}
          {pageStage === 'error' && <ErrorMessage />}
        </div>
      </HeaderFooterLayout>
    </AuthedPage>
  );
};

const InviteColleagueForm = (props: {
  setPageState: (value: 'invite' | 'success' | 'error') => void;
}) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<InviteOrgColleagueFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });
  const inviteColleagueMutation = Organizations.useOrganizationInvite({
    mutation: {
      onSuccess: () => {
        props.setPageState('success');
      },
      onError: () => {
        props.setPageState('error');
      },
    },
  });
  return (
    <>
      <div className='w-full md:w-[700px] text-lg text-center mb-3'>
        Add the details below, and we will add your team member to your organization's account on
        MovingWorlds so that they can post and manage projects.
      </div>
      <form
        className='w-full md:w-[540px] flex flex-col gap-4'
        onSubmit={handleSubmit((data) => {
          inviteColleagueMutation.mutate({
            data: {
              firstname: data.firstname,
              lastname: data.lastname,
              email: data.email,
              makeAdmin: data.makeAdmin ?? false,
              removeRepresentative: data.removeRepresentative ?? false,
            },
          });
          props.setPageState('success');
        })}
      >
        <InlineFormInput
          label='First Name'
          placeholder="Enter your colleague's first name"
          register={() => register('firstname', { required: 'Enter a first name.' })}
          errors={errors.firstname}
        />
        <InlineFormInput
          label='Last Name'
          placeholder="Enter your colleague's last name"
          register={() => register('lastname', { required: 'Enter a last name.' })}
          errors={errors.lastname}
        />
        <InlineFormInput
          label='Email'
          placeholder="Enter your colleague's email address"
          register={() =>
            register('email', {
              required: 'Enter an email address.',
              pattern: {
                value: /^\S+@\S+$/i,
                message: 'Please enter a valid email address.',
              },
            })
          }
          errors={errors.email}
        />
        <FormsCheckBox
          size='md'
          control={control}
          name='makeAdmin'
          aria-label='make new user admin checkbox'
          description='Make the new member an owner of all past projects and introductions'
        />
        <FormsCheckBox
          size='md'
          control={control}
          name='removeRepresentative'
          aria-label='make new user admin checkbox'
          description='Remove me from the organization after the new member joins'
        />
        <p className='italic'>
          By submitting this form, you give MovingWorlds consent to email your team member. We will
          NOT add the above email to any sales or marketing lists, and we never sell data.
        </p>
        <Button type='submit' size='md' variant='primary' additionalClassnames='w-fit mx-auto'>
          Invite colleague
        </Button>
      </form>
    </>
  );
};

const ConfirmationMessage = () => {
  return (
    <div className='w-full md:w-[800px] text-lg text-center flex flex-col items-center gap-4'>
      <GreenCircleCheckmark />
      <p>
        Your response has been successfully submitted! Your colleague has been invited to join your
        organization. They will receive an email with instructions on how to accept the invitation.
      </p>
      <p>Thank you for helping us grow the MovingWorlds community!</p>
      <Link href={ROUTES.DASHBOARD} className='hover:underline text-brand-500'>
        Return to your dashboard
      </Link>
    </div>
  );
};

const ErrorMessage = () => {
  return (
    <div className='w-full md:w-[540px] text-lg text-center flex flex-col items-center gap-4'>
      <p>There was an error processing your request. Please try again later.</p>
      <Link href={ROUTES.DASHBOARD} className='hover:underline text-brand-500'>
        Return to your dashboard
      </Link>
    </div>
  );
};
